// src/pages/AcompanhamentoGestantes.js

import React, { useState, useEffect, useMemo } from 'react';
import { Search, User, Stethoscope, Heart, Scale, FlaskConical, Beaker, Smile, Baby, Calendar, ChevronLeft, ChevronRight, MapPin, Filter, XCircle, CheckCircle, Check, Square, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import StatusIndicator from '../components/StatusIndicator';
import FilterControls from '../components/FilterControls';
import MonitoringCharts from '../components/MonitoringCharts';
import { usePatientFilters } from '../hooks/usePatientFilters';
import { apiFetch } from '../utils/apiFetch';
import './Acompanhamento.css'; // green theme 

// --- HELPERS (Padronização com as outras páginas) ---

const calculateAge = (dtNascimento) => {
    if (!dtNascimento) return '';
    const birthDate = new Date(dtNascimento);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
};

const getAddressKey = (p) => {
    const rua = (p.endereco || 'ZZ_SEM_ENDERECO').trim().toLowerCase();
    const num = (p.numero || 'S/N').trim().toLowerCase();
    const comp = (p.complemento || '').trim().toLowerCase();
    return `${rua}|${num}|${comp}`;
};

const formatAddressDisplay = (p) => {
    if (!p.endereco) return "Endereço Não Informado";
    const rua = p.endereco.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
    const num = p.numero ? `, ${p.numero}` : '';
    const comp = p.complemento ? ` (${p.complemento})` : '';
    return `${rua}${num}${comp}`;
};

// Configuração dos Gráficos
const CHART_CONFIG_GESTANTES = [
    { id: 'consultasTotal', label: 'Consultas (7x)', icon: <Stethoscope size={16}/> },
    { id: 'captacaoPrecoce', label: 'Captação < 12sem', icon: <Calendar size={16}/> },
    { id: 'monitoramento', label: 'PA e Peso', icon: <Scale size={16}/> },
    { id: 'visitasACS', label: 'Visitas ACS', icon: <User size={16}/> },
    { id: 'testesRapidos', label: 'Testes Rápidos', icon: <Beaker size={16}/> },
    { id: 'vacinaDtpa', label: 'Vacina dTpa', icon: <FlaskConical size={16}/> },
    { id: 'consultaOdonto', label: 'Odonto', icon: <Smile size={16}/> },
    { id: 'puerperio', label: 'Puerpério', icon: <Baby size={16}/> },
];

const ColumnHeader = ({ label, icon: Icon, filterState, onClick, width }) => {
    const isActive = filterState?.key; 
    const type = filterState?.type;

    let StatusIcon = Filter;
    let iconColor = "#94a3b8";

    if (isActive) {
        if (type === 'pending') {
            StatusIcon = XCircle;
            iconColor = "#ef4444";
        } else if (type === 'completed') {
            StatusIcon = CheckCircle;
            iconColor = "#22c55e";
        }
    }

    return (
        <div 
            className={`col-atividade header-clickable ${isActive ? 'active-filter' : ''}`} 
            onClick={onClick}
            title={isActive ? (type === 'pending' ? "Filtrando Pendentes" : "Filtrando Realizados") : "Filtrar Pendentes"}
            style={{ cursor: 'pointer', userSelect: 'none', width: width }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                <Icon size={14} />
                <span className="header-label-text">{label}</span>
                <StatusIcon size={12} fill={isActive ? "white" : "none"} color={iconColor} className={!isActive ? "filter-icon-hover" : ""} />
            </div>
        </div>
    );
};

export default function AcompanhamentoGestantes() {
    const navigate = useNavigate();
    const [gestantes, setGestantes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    
    // Estado de Seleção
    const [selectedPatients, setSelectedPatients] = useState(new Set());

    const {
        searchTerm, setSearchTerm,
        sortConfig, setSortConfig,
        activeAlerts, setActiveAlerts,
        selectedMicroArea, setSelectedMicroArea,
        statusFilter, setStatusFilter,
        filteredList: filteredGestantes,
    } = usePatientFilters(
        gestantes, 
        ['nome', 'cns', 'endereco'], 
        { key: 'nome', direction: 'ascending' }
    );

    const availableMicroAreas = useMemo(() => {
        const areas = new Set(gestantes.map(p => p.micro_area).filter(Boolean));
        return Array.from(areas).sort();
    }, [gestantes]);

    useEffect(() => { setCurrentPage(1); }, [searchTerm, activeAlerts, selectedMicroArea, statusFilter]);

    // Recuperar seleção salva
    useEffect(() => {
        const saved = localStorage.getItem('selectedGestantes');
        if (saved) {
            try { setSelectedPatients(new Set(JSON.parse(saved))); } catch (e) { console.error(e); }
        }
    }, []);

    useEffect(() => {
        const fetchPacientes = async () => {
            setLoading(true);
            try {
                const response = await apiFetch('/api/v2/gestantes'); 
                const result = await response.json();
                const allPatients = result.data || [];

                const formatarData = (dataSql) => {
                    if (!dataSql) return '';
                    const d = new Date(dataSql);
                    return new Date(d.getTime() + d.getTimezoneOffset() * 60000).toLocaleDateString('pt-BR');
                };

                const buildStatus = (statusBanco, textoEmDia, textoPendente) => {
                    if (statusBanco === 'NAO_APLICA') return { status: 'neutral', text: '-' };
                    if (statusBanco === 'FALTA_DUM') return { status: 'critical', text: 'Sem DUM' };
                    if (statusBanco === 'PENDENTE_PRAZO') return { status: 'neutral', text: 'No Prazo' };
                    
                    const emDia = statusBanco === 'EM_DIA';
                    return {
                        target: 1,
                        current: emDia ? 1 : 0,
                        status: emDia ? 'ok' : 'critical',
                        text: emDia ? textoEmDia : textoPendente
                    };
                };

                const gestantesData = allPatients.map(p => {
                    // Cálculo da meta progressiva para a string do front-end
                    const metaConsultas = p.semanas_gestacao ? Math.min(7, Math.floor(p.semanas_gestacao / 4)) : 1;
                    const metaVisitas = p.semanas_gestacao ? Math.min(3, Math.floor(p.semanas_gestacao / 10)) : 1;

                    const statusAtualizado = {
                        consultasTotal: buildStatus(p.status_consultas, `${p.total_consultas}/${metaConsultas} feitas`, 'Atrasada'),
                        captacaoPrecoce: buildStatus(p.status_captacao, 'Até 12ª Sem', 'Atrasada'),
                        monitoramento: buildStatus(p.status_monitoramento, 'Em Dia', 'Atrasado'),
                        visitasACS: buildStatus(p.status_visitas, `${p.total_visitas}/${metaVisitas} feitas`, 'Atrasada'),
                        testesRapidos: buildStatus(p.status_testes, 'Em Dia', 'Atrasado'),
                        vacinaDtpa: buildStatus(p.status_dtpa, 'Aplicada', 'Atrasada'),
                        consultaOdonto: buildStatus(p.status_odonto, 'Feita', 'Pendente'),
                        puerperio: buildStatus(p.status_puerperio, 'Em Dia', 'Atrasado'),
                        score: 0
                    };

                    // Pontuação - Divisão do Peso (cada item vale ~11 pts)
                    if (p.status_consultas === 'EM_DIA') statusAtualizado.score += 11;
                    if (p.status_captacao === 'EM_DIA') statusAtualizado.score += 11;
                    if (p.status_monitoramento === 'EM_DIA') statusAtualizado.score += 11;
                    if (p.status_visitas === 'EM_DIA') statusAtualizado.score += 11;
                    if (p.status_testes === 'EM_DIA') statusAtualizado.score += 11;
                    if (p.status_dtpa === 'EM_DIA') statusAtualizado.score += 11;
                    if (p.status_odonto === 'EM_DIA') statusAtualizado.score += 11;
                    if (p.status_puerperio === 'EM_DIA') statusAtualizado.score += 23; // Peso maior pro puerpério fechar os 100

                    return { 
                        ...p, 
                        idade: calculateAge(p.dt_nascimento),
                        dum: formatarData(p.dum),
                        data_parto: formatarData(p.data_parto),
                        status: statusAtualizado 
                    };
                });

                setGestantes(gestantesData);
            } catch (error) {
                console.error("Erro ao buscar gestantes da V2:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchPacientes();
    }, []);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredGestantes.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredGestantes.length / itemsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            document.querySelector('.acompanhamento-container')?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const getFilterState = (key) => statusFilter.key === key ? statusFilter : null;

    // --- LÓGICA DE SELEÇÃO E ENVIO ---

    const togglePatientSelection = (cns) => {
        setSelectedPatients(prev => {
            const newSet = new Set(prev);
            if (newSet.has(cns)) newSet.delete(cns);
            else newSet.add(cns);
            localStorage.setItem('selectedGestantes', JSON.stringify([...newSet]));
            return newSet;
        });
    };

    const toggleAllOnPage = () => {
        const allKeysOnPage = currentItems.map(p => p.cns);
        const allSelected = allKeysOnPage.every(key => selectedPatients.has(key));
        
        setSelectedPatients(prev => {
            const newSet = new Set(prev);
            if (allSelected) allKeysOnPage.forEach(key => newSet.delete(key));
            else allKeysOnPage.forEach(key => newSet.add(key));
            localStorage.setItem('selectedGestantes', JSON.stringify([...newSet]));
            return newSet;
        });
    };

    const clearSelection = () => {
        setSelectedPatients(new Set());
        localStorage.removeItem('selectedGestantes');
    };

    const sendToVisitPage = () => {
        if (selectedPatients.size === 0) {
            alert('Selecione ao menos uma gestante!');
            return;
        }

        const selectedData = gestantes.filter(p => selectedPatients.has(p.cns));
        
        // Agrupamento por Endereço
        const groups = {};
        selectedData.forEach(p => {
            const key = getAddressKey(p);
            if (!groups[key]) {
                groups[key] = {
                    key: key,
                    addressDisplay: formatAddressDisplay(p),
                    members: []
                };
            }
            groups[key].members.push(p);
        });

        const visitData = Object.values(groups);
        localStorage.setItem('visitQueueData', JSON.stringify(visitData));
        navigate('/folha-visita');
    };

    const allOnPageSelected = currentItems.length > 0 && 
        currentItems.every(p => selectedPatients.has(p.cns));


    return (
                <div className="acs-page">
            <header className="acs-header">
                <div className="acs-header-left">
                    <div className="acs-header-icon-row">
                        <div className="acs-header-icon-box">
                            <Search size={18} color="white" />
                        </div>
                        <h1>Gestantes e Puérperas</h1>
                    </div>
                    <p className="acs-header-sub">
                        Gerenciamento de <strong>{filteredGestantes.length}</strong> pacientes
                        {selectedPatients.size > 0 && (
                            <span className="acs-selected-badge">
                                ✓ {selectedPatients.size} selecionado(s)
                            </span>
                        )}
                    </p>
                    <div className="acs-search">
                        <Search size={16} color="rgba(255,255,255,0.7)" />
                        <input
                            type="text"
                            placeholder="Buscar por nome, cartão SUS ou endereço..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                {!loading && gestantes.length > 0 && (
                    <div className="acs-header-right">
                        <MonitoringCharts patients={filteredGestantes} chartConfig={CHART_CONFIG_GESTANTES} />
                    </div>
                )}
            </header>

            <FilterControls
                sortConfig={sortConfig}
                setSortConfig={setSortConfig}
                activeAlerts={activeAlerts}
                setActiveAlerts={setActiveAlerts}
                microAreas={availableMicroAreas}
                selectedMicroArea={selectedMicroArea}
                setSelectedMicroArea={setSelectedMicroArea}
                hiddenFilters={['Idoso', 'Gestante', 'Acamado,Domiciliado']} 
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
            />

            <div className="acompanhamento-container" style={{ margin: '24px' }}>
                 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', fontSize: '0.9rem', color: '#64748b' }}>
                    <div>Exibindo {filteredGestantes.length > 0 ? indexOfFirstItem + 1 : 0}-{Math.min(indexOfLastItem, filteredGestantes.length)} de {filteredGestantes.length}</div>
                    
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        {selectedPatients.size > 0 && (
                            <>
                                <button 
                                    onClick={sendToVisitPage}
                                    style={{
                                        padding: '8px 16px', background: 'linear-gradient(135deg, #059669, #047857)', color: 'white', border: 'none', borderRadius: '6px',
                                        cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px'
                                    }}
                                >
                                    Enviar para Folha de Visita ({selectedPatients.size})
                                </button>
                                <button 
                                    onClick={clearSelection}
                                    className="btn-clear"
                                >
                                    Limpar Seleção
                                </button>
                            </>
                        )}
                        <select 
                            value={itemsPerPage} 
                            onChange={(e) => setItemsPerPage(Number(e.target.value))}
                            className="acs-per-page"
                        >
                            <option value={10}>10 por página</option>
                            <option value={20}>20 por página</option>
                            <option value={50}>50 por página</option>
                        </select>
                    </div>
                </div>

                <div className="acompanhamento-header">
                    {/* Checkbox Header */}
                    <div className="col-checkbox" style={{ width: '50px', display: 'flex', justifyContent: 'center' }}>
                        <div 
                            onClick={toggleAllOnPage}
                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            title="Selecionar todos da página"
                        >
                            {allOnPageSelected && currentItems.length > 0 ? 
                                <Check size={18} color="#059669" strokeWidth={3} /> : 
                                <Square size={18} color="#94a3b8" />
                            }
                        </div>
                    </div>

                    <div className="col-paciente" style={{width: '25%'}}><User size={14} /> Paciente / DUM</div>
                    <ColumnHeader label="Cons(7)" icon={Stethoscope} filterState={getFilterState('consultasTotal')} onClick={() => setStatusFilter('consultasTotal')} />
                    <ColumnHeader label="Captação" icon={Calendar} filterState={getFilterState('captacaoPrecoce')} onClick={() => setStatusFilter('captacaoPrecoce')} />
                    <ColumnHeader label="Monit." icon={Heart} filterState={getFilterState('monitoramento')} onClick={() => setStatusFilter('monitoramento')} />
                    <ColumnHeader label="Visitas" icon={User} filterState={getFilterState('visitasACS')} onClick={() => setStatusFilter('visitasACS')} />
                    <ColumnHeader label="Testes" icon={Beaker} filterState={getFilterState('testesRapidos')} onClick={() => setStatusFilter('testesRapidos')} />
                    <ColumnHeader label="dTpa" icon={FlaskConical} filterState={getFilterState('vacinaDtpa')} onClick={() => setStatusFilter('vacinaDtpa')} />
                    <ColumnHeader label="Odonto" icon={Smile} filterState={getFilterState('consultaOdonto')} onClick={() => setStatusFilter('consultaOdonto')} />
                    <ColumnHeader label="Puerp." icon={Baby} filterState={getFilterState('puerperio')} onClick={() => setStatusFilter('puerperio')} />
                </div>

                {loading ? ( <p style={{ padding: '20px' }}>Carregando...</p> ) : (
                    currentItems.map((patient) => {
                        const isSelected = selectedPatients.has(patient.cns);
                        return (
                            <div 
                                className={`acompanhamento-patient-row${isSelected ? ' selected' : ''}`}
                                key={patient.cns || patient['Cartão SUS'] || Math.random()}
                            >
                                {/* --- COLUNA DE SELEÇÃO ESTILIZADA (AVATAR) --- */}
                                <div className="col-checkbox" style={{ width: '50px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <div 
                                        onClick={() => togglePatientSelection(patient.cns)}
                                        style={{ 
                                            width: '32px', 
                                            height: '32px', 
                                            borderRadius: '50%', 
                                            background: isSelected ? 'linear-gradient(135deg, #059669, #047857)' : '#f1f5f9', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            color: isSelected ? 'white' : '#64748b', 
                                            fontSize: '0.75rem', 
                                            fontWeight: 'bold', 
                                            flexShrink: 0,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            border: isSelected ? '2px solid #065f46' : 'none'
                                        }}
                                        title={isSelected ? "Clique para desmarcar" : "Clique para selecionar"}
                                    >
                                        {isSelected ? <Check size={16} /> : patient.nome.charAt(0)}
                                    </div>
                                </div>

                                <div className="col-paciente" style={{width: '25%', display: 'flex', flexDirection: 'column', gap: '4px'}}>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                                        <span style={{ fontWeight: 600, color: '#334155' }}>{patient.nome}</span>
                                        {patient.status.score > 0 && (
                                            <span style={{
                                                fontSize: '0.7rem', fontWeight: 'bold', padding: '2px 8px', borderRadius: '12px',
                                                backgroundColor: patient.status.score >= 90 ? '#dcfce7' : '#fee2e2',
                                                color: patient.status.score >= 90 ? '#166534' : '#991b1b',
                                                border: '1px solid',
                                                borderColor: patient.status.score >= 90 ? '#86efac' : '#fecaca'
                                            }}>
                                                {patient.status.score} pts
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.75rem', color: '#64748b', flexWrap: 'wrap' }}>
                                        {patient.idade !== -1 && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '3px', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>
                                                <Clock size={10} />
                                                <span>{patient.idade} anos</span>
                                            </div>
                                        )}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px'}}>
                                            <MapPin size={10} />
                                            <span>
                                                {formatAddressDisplay(patient)} 
                                                {patient.micro_area && ` (MA: ${patient.micro_area})`}
                                            </span>
                                        </div>
                                    </div>
                                    {/* Exibe DUM ou Informação de Parto */}
                                    <div style={{ fontSize: '0.7rem', color: '#6366f1', fontWeight: 500 }}>
                                        {patient.dum && `DUM: ${patient.dum}`}
                                        {patient.dum && patient.data_parto && ' | '}
                                        {patient.data_parto && `Parto: ${patient.data_parto}`}
                                        {!patient.dum && !patient.data_parto && 'Sem registro de DUM'}
                                    </div>
                                </div>

                                <div className="col-atividade"><StatusIndicator status={patient.status.consultasTotal} /></div>
                                <div className="col-atividade"><StatusIndicator status={patient.status.captacaoPrecoce} /></div>
                                <div className="col-atividade"><StatusIndicator status={patient.status.monitoramento} /></div>
                                <div className="col-atividade"><StatusIndicator status={patient.status.visitasACS} /></div>
                                <div className="col-atividade"><StatusIndicator status={patient.status.testesRapidos} /></div>
                                <div className="col-atividade"><StatusIndicator status={patient.status.vacinaDtpa} /></div>
                                <div className="col-atividade"><StatusIndicator status={patient.status.consultaOdonto} /></div>
                                
                                <div className="col-atividade">
                                    {patient.status.puerperio === 'neutral' ? (
                                        <span style={{color: '#cbd5e1', fontSize: '1.2rem', userSelect: 'none'}}>-</span>
                                    ) : (
                                        <StatusIndicator status={patient.status.puerperio} />
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
                 {!loading && filteredGestantes.length === 0 && (
                    <div className="empty-state">
                         {statusFilter ? "Nenhuma gestante com pendência neste indicador." : "Nenhuma gestante encontrada."}
                    </div>
                )}
                
                {!loading && filteredGestantes.length > 0 && (
                    <div className="pagination-footer" style={{ display: 'flex', justifyContent: 'center', gap: '10px', padding: '20px', alignItems: 'center', borderTop: '1px solid #e2e8f0', marginTop: 'auto' }}>
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="pagination-btn"><ChevronLeft size={16} /></button>
                        <span style={{ fontSize: '0.9rem' }}>Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong></span>
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="pagination-btn"><ChevronRight size={16} /></button>
                    </div>
                )}
            </div>
        </div>
    );
}